/**
 * Quiz Constants
 * Centralized constants for quiz answers and flow control
 */

// Answer values
export const ANSWERS = {
  YES: 'Ja',
  NO: 'Nein',
  SKIP: '-',
} as const;

// Care level values
export const CARE_LEVELS = {
  NONE: 'Nein',
  LEVEL_1: '1',
  LEVEL_2: '2',
  LEVEL_3: '3',
  LEVEL_4: '4',
  LEVEL_5: '5',
} as const;

// Flow-stopping care levels (no reimbursement possible)
export const INVALID_CARE_LEVELS = [CARE_LEVELS.NONE, CARE_LEVELS.LEVEL_1] as const;

// Care period values
export const CARE_PERIODS = {
  UNDER_6_MONTHS: 'Unter 6 Monate',
  OVER_6_MONTHS: 'über 6 Monate',
} as const;

// Section keys
export const SECTION_KEYS = {
  CARE_RECIPIENT: 'careRecipient',
  LEGAL_REPRESENTATIVE: 'legalRepresentative',
  PREVENTED_CAREGIVER: 'preventedCaregiver',
  SUBSTITUTE_CAREGIVER: 'substituteCaregiver',
  COMMERCIAL_PROVIDER: 'commercialProvider',
  PRIVATE_INDIVIDUAL: 'privateIndividual',
} as const;

export type SectionKey = (typeof SECTION_KEYS)[keyof typeof SECTION_KEYS];

// Question types
export const QUESTION_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  BIRTHDAY: 'birthday',
  PHONE_NUMBER: 'phoneNumber',
  FILE: 'file',
  OPTION: 'option',
  SUBMIT: 'submit',
  CHECKBOX: 'checkbox',
  SIGNATURE: 'signature',
  RATE: 'rate',
  GROUP: 'group', // For date/duration/amount combined input
  CONSTANT: 'constant',
  I_AGREE: 'iAgree',
  I_HAVE: 'iHave',
  PAID: 'paid',
} as const;

// Question keys (for referencing questions programmatically)
export const QUESTION_KEYS = {
  // Care Recipient section
  BIRTHDAY: 'birthday',
  LEVEL_OF_CARE: 'levelOfCare',
  CERTIFICATE: 'certificate',
  LEGAL_REPRESENTATION: 'legalRepresentation', // isBetreuung - triggers legal rep section
  
  // Prevented Caregiver section
  IS_PROFESSIONAL_CARER: 'isProfessionalCarer',
  CARE_PERIOD: 'carePeriod',
  ABSENCE_START_DATE: 'absenceStartDate',
  ABSENCE_END_DATE: 'absenceEndDate',
  ABSENCE_REASON: 'absenceReason',
  
  // Substitute Caregiver section
  IS_PAID_BY_OTHERS: 'isPaidByOthers',
  IS_COMMERCIALLY_ACTIVE: 'isCommerciallyActive',
  RELATIONSHIP_TYPE: 'relationshipType',
  MARRIAGE_RELATIONSHIP: 'marriageRelationship',
  SAME_HOUSEHOLD: 'sameHousehold',
  HOW_FOUND: 'howFound',
  
  // Private Individual / Commercial Provider section
  AVAILABLE_DATES: 'availableDates',
  DURATION_OF_ABSENCE: 'durationOfAbsence',
  HAS_TRAVEL_COSTS: 'hasTravelCosts',
  DISTANCE_KM: 'distanceKm',
  ORIGIN_LOCATION: 'originLocation',
  DESTINATION_LOCATION: 'destinationLocation',
  TRAVEL_COST: 'travelCost',
  RECEIPT_UPLOAD: 'receiptUpload',
  HAS_EARNINGS_LOSS: 'hasEarningsLoss',
  EARNINGS_LOSS_AMOUNT: 'earningsLossAmount',
  ACCOUNT_HOLDER_NAME: 'accountHolderName',
  IBAN: 'iban',
  BANK: 'bank',
} as const;

// Validation rules
export const VALIDATION = {
  ZIP_CODE_LENGTH: 5,
  DATE_FORMAT_LENGTH: 10,
  MAX_FILE_SIZE_MB: 5,
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'application/pdf'] as string[],
} as const;

// Numeric questions with no fixed length. Other 'number' questions (zipCode)
// keep the fixed ZIP_CODE_LENGTH constraint.
export const FREE_LENGTH_NUMBER_KEYS: string[] = [
  QUESTION_KEYS.DISTANCE_KM,
  QUESTION_KEYS.TRAVEL_COST,
  QUESTION_KEYS.EARNINGS_LOSS_AMOUNT,
];

// ============================================================
// CONDITIONAL FLOW QUESTIONS
// Questions where the answer affects the flow (skips, resets, or stops)
// Used for development indicators
// ============================================================
export type ConditionalFlowType = 'skip' | 'reset' | 'flowStop' | 'sectionReset';

export interface ConditionalFlowInfo {
  questionKey: string;
  flowType: ConditionalFlowType;
  description: string;
  triggerAnswers: string[];
}

export const CONDITIONAL_FLOW_QUESTIONS: ConditionalFlowInfo[] = [
  // Care Recipient Section
  {
    questionKey: 'levelOfCare',
    flowType: 'flowStop',
    description: 'Flow stops if care level is "Nein" or "1"',
    triggerAnswers: [CARE_LEVELS.NONE, CARE_LEVELS.LEVEL_1],
  },
  {
    questionKey: QUESTION_KEYS.LEGAL_REPRESENTATION,
    flowType: 'sectionReset',
    description: 'Resets legal representative section when changed',
    triggerAnswers: [ANSWERS.YES, ANSWERS.NO],
  },
  
  // Prevented Caregiver Section
  {
    questionKey: QUESTION_KEYS.IS_PROFESSIONAL_CARER,
    flowType: 'flowStop',
    description: 'Flow stops if "Ja" (professional/commercial caregiver)',
    triggerAnswers: [ANSWERS.YES],
  },
  {
    questionKey: QUESTION_KEYS.CARE_PERIOD,
    flowType: 'flowStop',
    description: 'Flow stops if "Unter 6 Monate"',
    triggerAnswers: [CARE_PERIODS.UNDER_6_MONTHS],
  },
  {
    questionKey: QUESTION_KEYS.ABSENCE_START_DATE,
    flowType: 'sectionReset',
    description: 'Resets both private & commercial sections when changed',
    triggerAnswers: ['*'], // Any date change
  },
  {
    questionKey: QUESTION_KEYS.ABSENCE_END_DATE,
    flowType: 'sectionReset',
    description: 'Resets both private & commercial sections when changed',
    triggerAnswers: ['*'],
  },
  
  // Substitute Caregiver Section
  {
    questionKey: QUESTION_KEYS.IS_PAID_BY_OTHERS,
    flowType: 'skip',
    description: 'Skips relationship questions if "Ja"',
    triggerAnswers: [ANSWERS.YES],
  },
  {
    questionKey: QUESTION_KEYS.IS_COMMERCIALLY_ACTIVE,
    flowType: 'skip',
    description: 'Skips relationship questions if "Ja"',
    triggerAnswers: [ANSWERS.YES],
  },
  
  // Private Individual Section
  {
    questionKey: QUESTION_KEYS.HAS_TRAVEL_COSTS,
    flowType: 'skip',
    description: 'Skips travel questions if "Nein"',
    triggerAnswers: [ANSWERS.NO],
  },
  {
    questionKey: QUESTION_KEYS.HAS_EARNINGS_LOSS,
    flowType: 'skip',
    description: 'Skips earnings amount if "Nein"',
    triggerAnswers: [ANSWERS.NO],
  },
];

// Quick lookup set for checking if a question has conditional flow
export const CONDITIONAL_FLOW_QUESTION_KEYS = new Set(
  CONDITIONAL_FLOW_QUESTIONS.map(q => q.questionKey)
);

// Get flow info for a specific question
export const getConditionalFlowInfo = (questionKey: string): ConditionalFlowInfo | undefined => {
  return CONDITIONAL_FLOW_QUESTIONS.find(q => q.questionKey === questionKey);
};

